import * as React from "react";
import { cn } from "@/lib/utils";

export interface PartnerEventAnimationProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Partner logo URL (optional)
   * If provided, displays partner logo instead of eventName text
   */
  partnerLogo?: string;

  /**
   * Event name for display (optional)
   * Only shown if partnerLogo is not provided
   */
  eventName?: string;

  /**
   * Event URL or permanent link (required)
   * Must start with https:// for security
   */
  redirectUrl: string;

  /**
   * Preview mode - disables redirect for Storybook/testing
   * Set to true in non-production environments (Storybook, screenshots, tests)
   * @default false
   */
  preview?: boolean;
}

/**
 * PartnerEventAnimation - Full-screen transition animation for partner event redirects
 *
 * **Figma Specifications:**
 * - Background: brand-red (#C80815)
 * - ANYW3AR logo: 196×38px (w-[12.25rem] h-[2.375rem])
 * - Logo asset: /images/logo-typo-white.svg
 * - Text: 16px Montserrat Regular (text-[1rem] font-montserrat font-normal)
 * - Partner logo: 147×147px (w-[9.1875rem] h-[9.1875rem])
 * - Gap between elements: 32px (gap-[2rem])
 * - Mobile max-width: 343px (max-w-[21.4375rem])
 *
 * **Animation Sequence (5.5 seconds total):**
 * 1. 0-500ms: Initial wait (red background only)
 * 2. 500-1500ms: Logo drops with ease-out (1000ms)
 * 3. 1500-2000ms: Pause with logo centered (500ms)
 * 4. 2000-3000ms: Text fades in + pushes content up (1000ms)
 * 5. 3000-4000ms: Partner logo fades in + pushes up (1000ms)
 * 6. 4000-5500ms: Display complete state (1500ms wait)
 * 7. 5500ms: Auto-redirect
 *
 * **Accessibility:**
 * - Respects prefers-reduced-motion (instant redirect if enabled)
 * - ARIA live region for screen readers
 * - Proper role="status" for loading indication
 *
 * @example
 * ```tsx
 * // With partner logo
 * <PartnerEventAnimation
 *   partnerLogo="https://example.com/logo.png"
 *   eventName="Summer Collection Launch"
 *   redirectUrl="https://partner.com/event"
 * />
 *
 * // Without logo (shows event name)
 * <PartnerEventAnimation
 *   eventName="Summer Collection Launch"
 *   redirectUrl="https://partner.com/event"
 * />
 * ```
 */

type AnimationPhase = 'logo' | 'text' | 'partner' | 'complete';
export const PartnerEventAnimation = React.forwardRef<
  HTMLDivElement,
  PartnerEventAnimationProps
>(({ partnerLogo, eventName, redirectUrl, preview, className, ...props }, ref) => {
  // Animation phase state for push-up effect
  const [phase, setPhase] = React.useState<AnimationPhase>('logo');

  // Validate redirect URL starts with https://
  React.useEffect(() => {
    if (!redirectUrl.startsWith("https://")) {
      console.error(
        `[PartnerEventAnimation] Invalid redirectUrl: ${redirectUrl}. Must start with https://`
      );
    }
  }, [redirectUrl]);

  // Check for prefers-reduced-motion
  const prefersReducedMotion = React.useMemo(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // Animation phase progression for push-up effect
  React.useEffect(() => {
    if (prefersReducedMotion) {
      setPhase('complete');
      return;
    }

    const timers = [
      setTimeout(() => setPhase('text'), 2000),     // 500ms wait + 1000ms logo + 500ms pause
      setTimeout(() => setPhase('partner'), 3000),  // + 1000ms text animation
      setTimeout(() => setPhase('complete'), 4000), // + 1000ms partner animation
    ];

    return () => timers.forEach(clearTimeout);
  }, [prefersReducedMotion]);

  // Auto-redirect after animation sequence
  React.useEffect(() => {
    // Don't redirect in preview mode (Storybook, screenshots, tests)
    if (preview) {
      console.log(
        `%c[Preview Mode] Redirect disabled`,
        'color: #FFA500; font-weight: bold;'
      );
      console.log(`Would redirect to: ${redirectUrl} after 5s`);
      return;
    }

    const redirectDelay = prefersReducedMotion ? 0 : 5500; // Instant if reduced motion, else 5.5s

    const timer = setTimeout(() => {
      window.location.href = redirectUrl;
    }, redirectDelay);

    return () => clearTimeout(timer);
  }, [redirectUrl, prefersReducedMotion, preview]);

  return (
    <div
      ref={ref}
      className={cn(
        "fixed inset-0 bg-brand-red flex items-start justify-center pt-[50vh]",
        className
      )}
      {...props}
    >
      {/* ARIA live region for screen readers */}
      <div role="status" aria-live="polite" className="sr-only">
        Redirecting to {eventName || "partner event"}
      </div>

      {/* Animation container - mobile max-width with push-up effect */}
      {/* Container starts at 50vh, then translates UP as elements appear */}
      {/* Goal: partner logo's TOP ends at screen center (50vh line) */}
      <div
        className={cn(
          "flex flex-col items-center gap-[2rem] max-w-[21.4375rem] px-8",
          "transition-transform duration-500 ease-out",
          // Push-up effect: container rises as elements appear
          // Logo height: 2.375rem, Gap: 2rem, Text height: ~1rem
          phase === 'logo' && "-translate-y-[1.1875rem]",   // Center logo (half logo height = 2.375/2)
          phase === 'text' && "-translate-y-[4.1875rem]",   // Push up by text + gap (1.1875 + 3)
          phase === 'partner' && "-translate-y-[7.375rem]", // Final: partner TOP at 50vh
          phase === 'complete' && "-translate-y-[7.375rem]"
        )}
      >
        {/* ANYW3AR logo - drops from top */}
        <img
          src="/images/logo-typo-white.svg"
          alt="ANYW3AR"
          className={cn(
            "w-[12.25rem] h-[2.375rem]",
            prefersReducedMotion
              ? "opacity-100"
              : "opacity-0 animate-[logo-drop_1s_ease-out_0.5s_forwards]"
          )}
        />

        {/* "presents its partnership with" text - fades in */}
        <p
          className={cn(
            "text-[1rem] font-montserrat font-normal text-white text-center",
            prefersReducedMotion
              ? "opacity-100"
              : "opacity-0 animate-[fade-push-up_1s_ease-out_1.5s_forwards]"
          )}
        >
          presents its partnership with
        </p>

        {/* Partner logo OR event name - fades in */}
        {partnerLogo ? (
          <img
            src={partnerLogo}
            alt={eventName || "Partner"}
            className={cn(
              "w-[9.1875rem] h-[9.1875rem] object-contain",
              prefersReducedMotion
                ? "opacity-100"
                : "opacity-0 animate-[fade-push-up_1s_ease-out_2.5s_forwards]"
            )}
          />
        ) : eventName ? (
          <p
            className={cn(
              "text-[1rem] font-montserrat font-normal text-white text-center",
              prefersReducedMotion
                ? "opacity-100"
                : "opacity-0 animate-[fade-push-up_1s_ease-out_2.5s_forwards]"
            )}
          >
            {eventName}
          </p>
        ) : null}
      </div>
    </div>
  );
});

PartnerEventAnimation.displayName = "PartnerEventAnimation";
